import { patchData, fetchData, postData } from 'modules/Axios';
import { notifyFailure, notifySuccess } from 'modules/Notifications';

export const defaultState = {
  isNameUnique: true,
  audiences: [],
  audiencesLoading: true,
};

export const SET_CAMPAIGN_AUDIENCES_LOADING = 'SET_CAMPAIGN_AUDIENCES_LOADING';
export const setCampaignAudiencesLoading = (payload) => ({
  type: SET_CAMPAIGN_AUDIENCES_LOADING,
  payload,
});

export const RECEIVE_CAMPAIGN_AUDIENCES = 'RECEIVE_CAMPAIGN_AUDIENCES';
export const receiveCampaignAudiences = (payload) => ({
  type: RECEIVE_CAMPAIGN_AUDIENCES,
  payload,
});

export const getCampaignAudiences = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const URL = `brands/${brand.id}/audiences?size=100000&status=DONE`;
    try {
      dispatch(setCampaignAudiencesLoading(true));
      const response = await dispatch(fetchData(URL));
      dispatch(receiveCampaignAudiences(response?.content ?? []));
    } catch (e) {
      console.error('API Call to /audiences failed...', e);
      notifyFailure('Failed to retrieve audiences list');
      dispatch(receiveCampaignAudiences([]));
    }
  };
};

export const getCampaignIntegrations = () => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const URL = `/brands/${brand.id}/campaigns/integrations`;
    try {
      const integrations = await dispatch(fetchData(URL));
      return integrations;
    } catch (e) {
      console.error('API Call to brands/{brandUuid}/campaigns/integrations failed...', e);
      notifyFailure('Failed to retrieve list of campaign integrations');
      return null;
    }
  };
};

export const createCampaign = (requestBody) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const URL = `/brands/${brand.id}/campaigns`;
    try {
      const campaign = await dispatch(postData(URL, requestBody));
      notifySuccess('Campaign Saved Successfully');
      return campaign;
    } catch (e) {
      console.error('API Call to create campaign failed...', e);
      notifyFailure('Failed to create campaign');
      return null;
    }
  };
};

export const duplicateCampaign = (opts) => {
  return async (dispatch, getState) => {
    const { brand: { id: brandId } } = getState().app;
    const { campaignId } = opts;
    const URL = `/brands/${brandId}/campaigns/${campaignId}/duplicate`;
    try {
      const campaign = await dispatch(postData(URL));
      notifySuccess('Campaign Duplicated Successfully');
      return campaign;
    } catch (e) {
      console.error('API Call to duplicate campaign failed...', e);
      notifyFailure('Failed to duplicate campaign');
      return null;
    }
  };
};

export const fetchCampaign = (id) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const URL = `/brands/${brand.id}/campaigns/${id}`;
    try {
      const campaign = await dispatch(fetchData(URL));
      return campaign;
    } catch (e) {
      notifyFailure('Failed to retrieve campaign details');
      return null;
    }
  };
};

export const updateCampaign = (requestBody, campaignId) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const URL = `/brands/${brand.id}/campaigns/${campaignId}`;
    try {
      const campaign = await dispatch(patchData(URL, requestBody));
      notifySuccess('Campaign Saved Successfully');
      return campaign;
    } catch (e) {
      console.error('API Call to save campaign failed...', e);
      notifyFailure('Failed to update campaign');
      return null;
    }
  };
};

export const onboardCampaign = (campaignId) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const URL = `/brands/${brand.id}/campaigns/${campaignId}/onboard`;
    try {
      await dispatch(postData(URL));
      notifySuccess('The onboarding process for has been started.');
      return true;
    } catch (e) {
      console.error('API Call to onboard campaign failed...', e);
      const errMessages = {
        start_date: 'Invalid start date. Please choose a date that is not in the past and is at least 48 hours from today.',
        autorefresh: 'Auto Refresh issue. Please update the campaign duration.  ',
        audiences: 'Audience issue. Please create a new campaign and try again.',
        integration_ids: 'Destination issue. Please create a new campaign and try again.',
        default: 'Issue trying to onboard campaign. Please try again later',
      };
      const errorMatch = e.response?.data?.message?.match(/fieldName=(.*?)(,|\))/);
      const errorMessage = errMessages[errorMatch[1]] ?? errMessages.default;
      notifyFailure(errorMessage);
      return null;
    }
  };
};

export const cancelOnboarding = (campaignId) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const URL = `/brands/${brand.id}/campaigns/${campaignId}/cancel-onboard`;
    try {
      await dispatch(postData(URL));
      return true;
    } catch (e) {
      console.error(e);
      notifyFailure('Onboarding cancellation failed. The cancellation period has expired.');
      return false;
    }
  };
};

export const CHECK_CAMPAIGN_NAME_UNIQUENESS = 'CHECK_CAMPAIGN_NAME_UNIQUENESS';
export const isNameUnique = (payload) => ({
  type: CHECK_CAMPAIGN_NAME_UNIQUENESS,
  payload,
});

export const checkNameUniqueness = (name) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    if (!name) return dispatch(isNameUnique(true));
    try {
      const result = await dispatch(postData(`brands/${brand.id}/campaigns/name-uniqueness`, { name }));
      dispatch(isNameUnique(result.unique));
    } catch (e) {
      console.error('API Call to check campaign name uniqueness failed...', e);
      notifyFailure('Failed to check name uniqueness for campaign');
    }
    return null;
  };
};

export const RESET_CREATE_CAMPAIGN_ROUTE_DATA = 'RESET_CREATE_CAMPAIGN_ROUTE_DATA';
export const resetRouteData = () => ({
  type: RESET_CREATE_CAMPAIGN_ROUTE_DATA,
});

export default function reducer(state = defaultState, action = {}) {
  switch (action.type) {
    case SET_CAMPAIGN_AUDIENCES_LOADING:
      return { ...state, audiencesLoading: action.payload };
    case RECEIVE_CAMPAIGN_AUDIENCES:
      return { ...state, audiences: action.payload, audiencesLoading: false };
    case RESET_CREATE_CAMPAIGN_ROUTE_DATA:
      return { ...defaultState };
    case CHECK_CAMPAIGN_NAME_UNIQUENESS:
      return { ...state, isNameUnique: action.payload };
    default:
      return state;
  }
}
